<template>
  <VueDraggableResizable
    class="window"
    :class="{ 'on-top': index === 0, invisible: !internalVisible, hide }"
    :style="{ 'z-index': `${10 - index}` }"
    :x="getInitialPosition()[0]"
    :y="getInitialPosition()[1]"
    :w="getInitialSize()[0]"
    :h="getInitialSize()[1]"
    :min-width="800"
    :min-height="450"
    :drag-handle="'.top-bar'"
    :handles="['tl', 'tr', 'br', 'bl']"
    :prevent-deactivation="true"
    :lock-aspect-ratio="true"
    :resizable="true"
    @dragging="onDrag"
  >
    <div class="content" @mousedown="onActive">
      <div class="top-bar">
        <img class="icon" :src="iconSrc" />
        <div class="title">{{ title }}</div>
        <button class="minimize-button" @click="onMinimizeClick">
          <span class="material-icons">minimize</span>
        </button>
      </div>
      <div class="main-content">
        <slot></slot>
      </div>
    </div>
  </VueDraggableResizable>
</template>

<script>
import VueDraggableResizable from "vue-draggable-resizable";
import windowStateStore, {
  onTaskBarItemClick,
  bringWindowToFront,
  minimizeWindow,
} from "@/stores/WindowStateStore";

export default {
  name: "Window",
  components: { VueDraggableResizable },
  props: {
    windowName: String,
    title: String,
    iconSrc: String,
  },
  data() {
    return {
      hide: true,
      internalVisible: false,
    };
  },
  computed: {
    visible() {
      return windowStateStore.visibleWindows.includes(this.windowName);
    },
    index() {
      return windowStateStore.visibleWindows.indexOf(this.windowName);
    },
  },
  methods: {
    getInitialSize() {
      let width = Math.round(window.innerWidth * 0.75);
      let height = Math.round(window.innerHeight * 0.75);

      if (width * (9 / 16) <= height) {
        height = width * (9 / 16);
      } else if (height * (16 / 9) <= width) {
        width = height * (16 / 9);
      }

      width = Math.round(width);
      height = Math.round(height);

      return [width, height];
    },
    getInitialPosition() {
      const offsets = { drone: 0, email: 10, news: 20, social: 30 }[
        this.windowName
      ];

      return [
        Math.max((window.innerWidth - this.getInitialSize()[0]) / 2, 0) +
          offsets,
        Math.max((window.innerHeight - this.getInitialSize()[1]) / 2, 0) +
          offsets,
      ];
    },
    onActive() {
      bringWindowToFront(this.windowName);
    },
    onMinimizeClick() {
      minimizeWindow(this.windowName);
    },
    onDrag: function(x, y) {
      windowStateStore.windowsSize[this.windowName] = [x, y];
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        if (value) {
          this.hide = false;
          setTimeout(() => {
            this.internalVisible = true;
          }, 50);
        } else {
          this.internalVisible = false;
          setTimeout(() => {
            this.hide = true;
          }, 100);
        }
      },
    },
  },
};
</script>

<style scoped lang="scss">
.window {
  border: 1px solid black;
  background-color: lightgray;
  opacity: 0.8;

  transition-property: opacity, transform, visibility;
  transition-duration: 100ms;
  transition-timing-function: ease-in-out;

  &.on-top {
    opacity: 1;
  }

  &.hide {
    display: none;
  }

  &.invisible {
    opacity: 0;
    transform: translateY(20px);
    visibility: hidden;
  }

  & > .content {
    height: 100%;
    width: 100%;

    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;

    & > .top-bar {
      width: 100%;
      height: 24px;
      background-color: rgb(147, 129, 182);

      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;

      .icon {
        height: 100%;
      }

      .minimize-button {
        height: 100%;
        padding: 0;
        margin: 0;
        border: none;
        background-color: white;

        &:active {
          background-color: gainsboro;
        }
      }
    }

    & > .main-content {
      flex: 1;
      width: 100%;
      overflow: hidden;
    }
  }
}

.window::v-deep .handle {
  display: block;
  position: absolute;
  z-index: 999999;
  width: 5px;
  height: 5px;

  &.handle-tl {
    top: 0;
    left: 0;
    cursor: nwse-resize;
  }

  &.handle-tr {
    top: 0;
    right: 0;
    cursor: nesw-resize;
  }

  &.handle-bl {
    bottom: 0;
    left: 0;
    cursor: nesw-resize;
  }

  &.handle-br {
    bottom: 0;
    right: 0;
    cursor: nwse-resize;
  }
}
</style>
